import { CalendarMonthOutlined, SendOutlined, ViewKanbanOutlined } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import FileDownloadSharpIcon from '@mui/icons-material/FileDownloadSharp';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, IconButton } from '@mui/material';
import type { ReactNode } from 'react';
import React from 'react';

import FuelSearchInput from '../fuel-search-input';
import VNDropdownButton from '../VNDropdownButton/VNDropdownButton';

export interface TableToolBarAction {
  key?: string;
  label: string;
  icon?: string;
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  alignRight?: boolean;
  type?: 'button' | 'search' | 'refresh' | 'export' | 'scheduleDisplay' | 'epgViewer';
  value?: string;
  render?: ReactNode;
}

interface TableToolbarProps {
  actions?: TableToolBarAction[];
  bulkActions?: TableToolBarAction[];
  showBulkMenu?: boolean;
  bulkMenuLabel?: string;
  onActionClick: (key: string) => void;
  onSearchTextChanged?: (value: string) => void;
}

const ICONS: Record<string, JSX.Element> = {
  filter: <FilterListIcon />,
  search: <SearchIcon />,
  export: <FileDownloadSharpIcon />,
  density: <DensityMediumIcon />,
  add: <AddIcon />,
  send: <SendOutlined />,
  calendar: <CalendarMonthOutlined />,
};

const TableToolbar: React.FC<TableToolbarProps> = ({
  actions = [],
  bulkActions = [],
  showBulkMenu = true,
  bulkMenuLabel = 'BULK OPTIONS',
  onActionClick,
  onSearchTextChanged,
}) => {
  const [hasSearch, setHasSearch] = React.useState(false);
  const handleSearchTextChanged = (value: string) => {
    setHasSearch(value.length > 0);
  };

  return (
    <Box
      className="vn-table-toolbar"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap" // Outer wrap is fine to prevent hard breaks on tiny screens
      padding="10px 0"
      marginBottom="10px"
      gap={2}
    >
      {/* Left Section: Bulk Options & Actions */}
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        sx={{ flex: '1 1 auto' }} // Takes up available space dynamically, but no longer forced to wrap internally
      >
        {showBulkMenu && bulkActions.length > 0 && (
          <Box display="flex" alignItems="center">
            <VNDropdownButton items={bulkActions} color="primary" onClick={(key) => onActionClick(key)} label={bulkMenuLabel} />
          </Box>
        )}

        {/* Removed flexWrap="wrap" here so Search and Density stay on the same line */}
        <Box display="flex" gap={1} alignItems="center">
          {actions
            .filter((action) => !action.alignRight)
            .map((action) => {
              if (action.render) {
                return <Box key={action.key}>{action.render}</Box>;
              }
              switch (action.type) {
                case 'search':
                  return (
                    <Box key={action.key} sx={{ width: '280px' }}>
                      <FuelSearchInput
                        onChange={onSearchTextChanged}
                        updateValue={handleSearchTextChanged}
                        onSearch={(value) => onActionClick(`${action.key}:${value}`)}
                        handleOnClear={() => onActionClick(`${action.key}:`)}
                        showClearIcon={hasSearch}
                        defaultValue={action.value}
                      />
                    </Box>
                  );
                case 'refresh':
                  return (
                    <div key={action.key}>
                      <i className="icon material-icons refresh-icon" onClick={() => onActionClick(action.key)}>
                        refresh
                      </i>
                    </div>
                  );
                default:
                  return (
                    <Button
                      key={action.key}
                      variant={action.variant || 'text'}
                      color={action.color}
                      startIcon={ICONS[action.icon]}
                      onClick={() => onActionClick(action.key)}
                    >
                      {action.label}
                    </Button>
                  );
              }
            })}
        </Box>
      </Box>

      {/* Right Section: alignRight Actions */}
      <Box
        display="flex"
        justifyContent="flex-end"
        gap={1}
        alignItems="center"
        sx={{ flexShrink: 0 }} // Prevents the right side icons from being crushed
      >
        {actions
          .filter((action) => action.alignRight)
          .map((action) => {
            if (action.render) {
              return <Box key={action.key}>{action.render}</Box>;
            }
            switch (action.type) {
              case 'search':
                return (
                  <FuelSearchInput
                    key={action.key}
                    onChange={onSearchTextChanged}
                    updateValue={handleSearchTextChanged}
                    onSearch={(value) => onActionClick(`${action.key}:${value}`)}
                    handleOnClear={() => onActionClick(`${action.key}:`)}
                    showClearIcon={hasSearch}
                  />
                );
              case 'refresh':
                return (
                  <div key={action.key} title="Refresh">
                    <i className="icon material-icons refresh-icon" onClick={() => onActionClick(action.key)}>
                      refresh
                    </i>
                  </div>
                );
              case 'epgViewer':
                return (
                  <IconButton title="Show EPG Viewer" onClick={() => onActionClick(action.key)}>
                    <ViewKanbanOutlined sx={{ transform: 'rotate(-90deg)' }} className="icon" />
                  </IconButton>
                );
              case 'export':
                return (
                  <div key={action.key} title={action.label}>
                    <i className="icon material-icons refresh-icon" onClick={() => onActionClick(action.key)}>
                      file_download
                    </i>
                  </div>
                );
              case 'scheduleDisplay':
                return (
                  <div key={action.key}>
                    <IconButton title="Show Schedule Inputs" onClick={() => onActionClick(action.key)}>
                      <CalendarMonthOutlined />
                    </IconButton>
                  </div>
                );
              default:
                return (
                  <Button
                    key={action.key}
                    variant={action.variant || 'text'}
                    color={action.color}
                    startIcon={ICONS[action.icon]}
                    onClick={() => onActionClick(action.key)}
                    size="small"
                  >
                    {action.label}
                  </Button>
                );
            }
          })}
      </Box>
    </Box>
  );
};

export default TableToolbar;
